import React, { createContext, useContext, useEffect, useMemo } from "react"
import { StyleProp, StyleSheet, View, ViewStyle } from "react-native"
import Animated, {
  Easing,
  cancelAnimation,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from "react-native-reanimated"
import { ThemeContext } from "../context/ThemeContext"

type SkeletonPulseConfig = {
  minOpacity: number
  maxOpacity: number
  durationMs: number
}

const SkeletonPulseContext = createContext<{
  progress: { value: number }
  minOpacity: number
  maxOpacity: number
  baseColor: string
} | null>(null)

const DEFAULT_PULSE: SkeletonPulseConfig = {
  minOpacity: 0.55,
  maxOpacity: 0.82,
  durationMs: 900,
}

const clamp01 = (n: number): number => Math.max(0, Math.min(1, n))

export const SkeletonGroup = ({
  children,
  minOpacity,
  maxOpacity,
  durationMs,
  color,
}: {
  children: React.ReactNode
  minOpacity?: number
  maxOpacity?: number
  durationMs?: number
  color?: string
}): JSX.Element => {
  const { theme } = useContext(ThemeContext)
  const resolvedMinOpacity = clamp01(minOpacity ?? DEFAULT_PULSE.minOpacity)
  const resolvedMaxOpacity = clamp01(maxOpacity ?? DEFAULT_PULSE.maxOpacity)
  const resolvedDurationMs = durationMs ?? DEFAULT_PULSE.durationMs

  const baseColor = useMemo(() => {
    return color || theme.colors.skeletonLoader
  }, [color, theme.colors.skeletonLoader])

  const progress = useSharedValue(0)

  useEffect(() => {
    progress.value = withRepeat(
      withTiming(1, {
        duration: resolvedDurationMs,
        easing: Easing.inOut(Easing.ease),
      }),
      -1,
      true
    )

    return () => {
      cancelAnimation(progress)
    }
  }, [progress, resolvedDurationMs])

  return (
    <SkeletonPulseContext.Provider
      value={{
        progress,
        minOpacity: resolvedMinOpacity,
        maxOpacity: resolvedMaxOpacity,
        baseColor,
      }}
    >
      {children}
    </SkeletonPulseContext.Provider>
  )
}

export const SkeletonBlock = ({
  style,
  radius = 10,
  color,
}: {
  style?: StyleProp<ViewStyle>
  radius?: number
  color?: string
}): JSX.Element => {
  const ctx = useContext(SkeletonPulseContext)
  const { theme } = useContext(ThemeContext)

  const baseColor = color || ctx?.baseColor || theme.colors.skeletonLoader
  const stableStyle = useMemo(() => {
    const flattened = StyleSheet.flatten(style)
    return flattened ? { ...flattened } : undefined
  }, [style])
  const animatedOpacityStyle = useAnimatedStyle(() => {
    if (!ctx) {
      return { opacity: 0.75 }
    }
    const range = ctx.maxOpacity - ctx.minOpacity
    return { opacity: ctx.minOpacity + (range * ctx.progress.value) }
  }, [ctx])

  if (!ctx) {
    // Fallback: non-animated skeleton block.
    return (
      <View
        style={[
          styles.block,
          { backgroundColor: baseColor, borderRadius: radius, opacity: 0.75 },
          stableStyle,
        ]}
      />
    )
  }

  return (
    <Animated.View
      style={[
        styles.block,
        { backgroundColor: baseColor, borderRadius: radius },
        animatedOpacityStyle,
        stableStyle,
      ]}
    />
  )
}

export const SkeletonLine = ({
  width = "100%",
  height = 12,
  radius = 999,
  style,
}: {
  width?: ViewStyle["width"]
  height?: number
  radius?: number
  style?: StyleProp<ViewStyle>
}): JSX.Element => {
  return <SkeletonBlock style={[{ width, height }, style]} radius={radius} />
}

export const SkeletonCircle = ({
  size = 44,
  style,
}: {
  size?: number
  style?: StyleProp<ViewStyle>
}): JSX.Element => {
  return <SkeletonBlock style={[{ width: size, height: size }, style]} radius={999} />
}

const styles = StyleSheet.create({
  block: {
    overflow: "hidden",
  },
})

